/**
 * Login → casino reveal.
 *
 * ChooseCasinos opens a casino (the last-visited one on login, or the player's pick) and
 * Home plays a short reveal instead of the old hard jump:
 *
 *  - Random Outcomes NOT yet shown this login session → the Random Outcomes disclaimer
 *    rises as a locked bottom drawer (80% open) over the dimmed property screen. Accept
 *    lowers it to the Explore drawer's resting position and the EGM cards ease in.
 *    The hand-off OVERLAPS the slide: the drawer starts fading out (and the cards and
 *    the Insight/Timeframe sections start revealing underneath) HANDOFF_LEAD_MS before
 *    the slide ends, so Accept → cards is ~0.8s instead of ~2.8s.
 *  - already shown this session → nothing (later casino entries skip it).
 *
 * Random Outcomes rule (every casino, every LinkPlayerflowVersion): once per logged-in
 * session, on the FIRST casino entry of that session (the login reopen of the last
 * casino, or the first manual pick when there is none). Accept continues, Decline
 * signs out. It shows again on every new login, even if accepted before. Tracked by a
 * sessionStorage marker keyed to the login (RANDOM_OUTCOMES_SESSION_KEY), cleared on
 * sign-in and logout. localStorage 'legalDisclaimer' is still written on Accept as a
 * record, but no longer decides whether it shows.
 *
 * This file is pure logic (no React): the session flag that hands the decision
 * from ChooseCasinos to Home, the per-session marker, the drawer phase machine, and
 * timing/geometry constants. The component lives in components/RandomOutcomesDrawer.
 */

export const LOGIN_CASINO_REVEAL_KEY = 'slotcheck.loginCasinoReveal';

export type LoginRevealMode = 'drawer' | 'fade';

/** 'pending' until ChooseCasinos decides to open the last casino or show selection. */
export type ResumeOutcome = 'pending' | 'entered' | 'selection';

// ---------------------------------------------------------------------------
// Geometry + timing (CSS in _m-random-outcomes-drawer.scss mirrors these).
// ---------------------------------------------------------------------------

/**
 * Fallback open height (80% of the screen) used by CSS until the drawer has been measured.
 * The real open height fits the content, capped at 90% (see src/lib/drawerFit.ts).
 */
export const DRAWER_OPEN_HEIGHT_FRACTION = 0.8;
/**
 * Where the Explore drawer rests today: ListSheet's MID snap puts its top at 50%
 * of the screen (MID_Y = screenHeight * 0.5). Accept lowers the disclaimer drawer
 * to this top edge so the hand-off to Explore's own drawer is seamless.
 */
export const EXPLORE_REST_TOP_FRACTION = 0.5;
/** Slide up: ease-out, ~0.7s. */
export const DRAWER_OPEN_MS = 700;
/** Slide down after Accept: snappy ease-out, 0.5s (was ease-in-out, 0.9s). */
export const DRAWER_CLOSE_MS = 500;
/** Easing of the Accept slide-down (the same ease-out the slide-up uses). */
export const DRAWER_CLOSE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
/**
 * Hand-off overlap: the drawer starts fading out, and the EGM cards + Insight/Timeframe
 * sections start revealing under it, this long BEFORE the slide-down ends. The drawer is
 * already ~92% of the way down by then (ease-out), so it never visibly "waits" at rest.
 */
export const HANDOFF_LEAD_MS = 300;
/** Extra pause at the rest position once Explore is ready (none: it is already underneath). */
export const HANDOFF_SETTLE_MS = 0;
/** Drawer cross-fades out over the revealing cards. */
export const HANDOFF_FADE_MS = 200;
/** The dimmed backdrop lifts over this long (must finish before the drawer is removed). */
export const BACKDROP_LIFT_MS = 400;
/** Never trap the user behind the spinner if Explore data never reports ready. */
export const WAIT_FOR_EXPLORE_MAX_MS = 15000;
/** EGM cards: fade + slight slide up, one by one (only the first CARD_STAGGER_ROWS stagger). */
export const CARD_STAGGER_MS = 50;
export const CARD_REVEAL_MS = 260;
export const CARD_STAGGER_ROWS = 8;
export const LOGIN_FADE_MS = 450;
export const REDUCED_MOTION_FADE_MS = 200;
/** Max rubber-band pull (px) when the user tries to drag the locked drawer down. */
export const RUBBER_BAND_MAX_PX = 28;

export type DrawerTiming = {
  open: number;
  close: number;
  /** How long before the slide-down ends the hand-off (fade-out + card reveal) begins. */
  handoffLead: number;
  handoffSettle: number;
  handoffFade: number;
  waitMax: number;
  cardStagger: number;
  cardReveal: number;
};

/** prefers-reduced-motion: slides become a quick fade, cards appear together. */
export function getDrawerTiming(reducedMotion: boolean): DrawerTiming {
  if (reducedMotion) {
    return {
      open: REDUCED_MOTION_FADE_MS,
      close: REDUCED_MOTION_FADE_MS,
      handoffLead: 0,
      handoffSettle: 0,
      handoffFade: 0,
      waitMax: WAIT_FOR_EXPLORE_MAX_MS,
      cardStagger: 0,
      cardReveal: REDUCED_MOTION_FADE_MS,
    };
  }
  return {
    open: DRAWER_OPEN_MS,
    close: DRAWER_CLOSE_MS,
    handoffLead: HANDOFF_LEAD_MS,
    handoffSettle: HANDOFF_SETTLE_MS,
    handoffFade: HANDOFF_FADE_MS,
    waitMax: WAIT_FOR_EXPLORE_MAX_MS,
    cardStagger: CARD_STAGGER_MS,
    cardReveal: CARD_REVEAL_MS,
  };
}

/** Time until every staggered card has finished (plus a small safety margin). */
export function cardRevealTotalMs(timing: DrawerTiming): number {
  return (CARD_STAGGER_ROWS - 1) * timing.cardStagger + timing.cardReveal + 100;
}

/**
 * Accept → 'closed' (phase 'waiting'): the slide keeps running underneath, the phase
 * only advances this long after Accept so the hand-off can overlap the slide's tail.
 */
export function closeToWaitingMs(timing: DrawerTiming): number {
  return Math.max(0, timing.close - timing.handoffLead);
}

/** Accept → the hand-off starts (drawer fades out, cards + sections start revealing). */
export function acceptToHandoffMs(timing: DrawerTiming): number {
  return closeToWaitingMs(timing) + timing.handoffSettle;
}

/** Accept → drawer removed (data ready: Explore was already up under it). */
export function acceptToDoneMs(timing: DrawerTiming): number {
  return acceptToHandoffMs(timing) + timing.handoffFade;
}

/** Accept → the first / the last staggered card has finished fading in (data ready). */
export function acceptToFirstCardDoneMs(timing: DrawerTiming): number {
  return acceptToHandoffMs(timing) + timing.cardReveal;
}
export function acceptToLastCardDoneMs(timing: DrawerTiming): number {
  return acceptToHandoffMs(timing) + (CARD_STAGGER_ROWS - 1) * timing.cardStagger + timing.cardReveal;
}

/** Distance (as a share of screen height) the drawer travels on Accept: 80% → rest. */
export function drawerLowerShift(): number {
  const openTop = 1 - DRAWER_OPEN_HEIGHT_FRACTION;
  return EXPLORE_REST_TOP_FRACTION - openTop;
}

/** Elastic pull for an attempted drag-down on the locked drawer. */
export function rubberBandOffset(dragDeltaY: number): number {
  if (!Number.isFinite(dragDeltaY) || dragDeltaY <= 0) return 0;
  return Math.min(RUBBER_BAND_MAX_PX, dragDeltaY * 0.35);
}

// ---------------------------------------------------------------------------
// Drawer phase machine.
// ---------------------------------------------------------------------------

export type DrawerPhase =
  | 'entering' // sliding up to 80%
  | 'open' // settled at 80%, waiting for Accept / Decline
  | 'closing' // Accept → lowering to the Explore rest position
  | 'waiting' // (nearly) at rest; holding for Explore's drawer to be ready
  | 'handoff' // fading out over Explore's own drawer; cards + sections reveal underneath
  | 'done';

export type DrawerEvent =
  | { type: 'entered' }
  | { type: 'accept' }
  | { type: 'closed' }
  | { type: 'handoff' }
  | { type: 'done' }
  // Attempts to leave the drawer without accepting. These never change phase.
  | { type: 'backdrop' }
  | { type: 'drag'; dy: number }
  | { type: 'swipe' }
  | { type: 'back' }
  | { type: 'escape' };

/**
 * The ONLY way out of entering/open is `accept` (Decline signs out instead and
 * never reaches the reducer). Backdrop taps, drags, swipes, the back gesture and
 * Escape are accepted as events but are deliberate no-ops.
 */
export function drawerReducer(phase: DrawerPhase, event: DrawerEvent): DrawerPhase {
  switch (event.type) {
    case 'entered':
      return phase === 'entering' ? 'open' : phase;
    case 'accept':
      return phase === 'entering' || phase === 'open' ? 'closing' : phase;
    case 'closed':
      return phase === 'closing' ? 'waiting' : phase;
    case 'handoff':
      return phase === 'waiting' ? 'handoff' : phase;
    case 'done':
      return phase === 'handoff' ? 'done' : phase;
    default:
      return phase;
  }
}

/** True while the user has not accepted yet (drawer is locked). */
export function isDrawerLocked(phase: DrawerPhase): boolean {
  return phase === 'entering' || phase === 'open';
}

// ---------------------------------------------------------------------------
// Session flag: ChooseCasinos → Home.
// ---------------------------------------------------------------------------

function defaultSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Not shown this login session → drawer; already shown → light fade only. */
export function resolveLoginRevealMode(shownThisSession: boolean): LoginRevealMode {
  return shownThisSession ? 'fade' : 'drawer';
}

/** Called by ChooseCasinos right before it opens a casino (only when it is due). */
export function markLoginCasinoReveal(
  mode: LoginRevealMode,
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.setItem(LOGIN_CASINO_REVEAL_KEY, mode);
  } catch {
    // blocked storage → no reveal (Home shows normally)
  }
}

export function peekLoginCasinoReveal(
  storage: Storage | null = defaultSessionStorage()
): LoginRevealMode | null {
  try {
    const v = storage?.getItem(LOGIN_CASINO_REVEAL_KEY);
    return v === 'drawer' || v === 'fade' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Cleared only once the user accepts / declines (drawer) or the fade has played.
 * It is NOT cleared on mount, so a reload before Accept shows the drawer again.
 */
export function clearLoginCasinoReveal(
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.removeItem(LOGIN_CASINO_REVEAL_KEY);
  } catch {
    // ignore
  }
}

/**
 * What Home does on mount / on a new casino entry. A stale 'drawer' flag after Random
 * Outcomes was already accepted this session degrades to the light fade; no flag →
 * nothing (normal Home).
 */
export function decideLoginReveal(input: {
  flag: LoginRevealMode | null;
  shownThisSession: boolean;
}): LoginRevealMode | 'none' {
  if (!input.flag) return 'none';
  if (input.flag === 'drawer' && input.shownThisSession) return 'fade';
  return input.flag;
}

// ---------------------------------------------------------------------------
// Random Outcomes: once per logged-in session (all casinos, all versions).
// ---------------------------------------------------------------------------

export const RANDOM_OUTCOMES_SESSION_KEY = 'slotcheck.randomOutcomesShown';

const loginKeyOf = (loginKey: string | number | null | undefined): string =>
  loginKey == null ? '' : String(loginKey).trim();

/**
 * Set when the player accepts Random Outcomes. Keyed to the login (appUserPk), so a
 * different account signing in on the same tab still sees it.
 */
export function markRandomOutcomesShownThisSession(
  loginKey: string | number | null | undefined,
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.setItem(RANDOM_OUTCOMES_SESSION_KEY, loginKeyOf(loginKey) || '1');
  } catch {
    // blocked storage → it shows again on the next entry (safe side)
  }
}

/** True once accepted in this login session (by this login, when the key is known). */
export function hasShownRandomOutcomesThisSession(
  loginKey: string | number | null | undefined,
  storage: Storage | null = defaultSessionStorage()
): boolean {
  try {
    const v = storage?.getItem(RANDOM_OUTCOMES_SESSION_KEY);
    if (!v) return false;
    const key = loginKeyOf(loginKey);
    return !key || v === key || v === '1';
  } catch {
    return false;
  }
}

/** Cleared on every sign-in and on logout, so each new login shows it again. */
export function clearRandomOutcomesSession(
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.removeItem(RANDOM_OUTCOMES_SESSION_KEY);
  } catch {
    // ignore
  }
}

/**
 * ChooseCasinos, on every casino entry (login reopen AND manual pick): the reveal to
 * hand to Home, or null when Random Outcomes was already shown this session.
 */
export function decideCasinoEntryReveal(shownThisSession: boolean): LoginRevealMode | null {
  return shownThisSession ? null : 'drawer';
}

// ---------------------------------------------------------------------------
// Display-only signal: true while the Random Outcomes drawer is on screen, so the
// Link Player drawer (Version 2) waits for it and the two never show together.
// ---------------------------------------------------------------------------

let randomOutcomesActive = false;
const roListeners = new Set<() => void>();

export function setRandomOutcomesActive(next: boolean): void {
  if (randomOutcomesActive === next) return;
  randomOutcomesActive = next;
  roListeners.forEach((l) => l());
}

export function getRandomOutcomesActive(): boolean {
  return randomOutcomesActive;
}

export function subscribeRandomOutcomesActive(listener: () => void): () => void {
  roListeners.add(listener);
  return () => {
    roListeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// ChooseCasinos: when the (legacy) Random Outcomes modal may open.
// ---------------------------------------------------------------------------

/**
 * The modal on Select Casino follows the same once-per-login-session rule as the
 * drawer, and never shows twice in a session:
 *
 *  - already shown (accepted) this session → never.
 *  - entered a casino → never (the drawer owns the disclaimer there).
 *  - a normal Select Casino visit → never: the first casino entry of the session shows
 *    the drawer in Explore (login reopen or first manual pick), so the modal would
 *    only duplicate it.
 *  - forceSelect (side menu / header logo) and not yet shown this session → modal.
 *    Accepting it marks the session, so the next pick does not show the drawer.
 */
export function shouldShowChooseCasinoDisclaimer(input: {
  shownThisSession: boolean;
  forceSelect: boolean;
  resumeOutcome: ResumeOutcome;
}): boolean {
  if (input.shownThisSession) return false;
  if (input.resumeOutcome === 'entered') return false;
  return input.forceSelect;
}
